import React, { useEffect, useRef, useMemo } from "react";
import ApexCharts from "apexcharts";
import { THEME_COLORS } from "../../../../../color";

const ProgressChartSupply = ({
  title = "",
  mainLabel = "",
  mainValue = 0,
  totalValue = 0,
  chartMainValue,
  chartTotalValue,
  primaryColor = THEME_COLORS.green,
  secondaryColor = "#EAF1EF",
  primaryLegendLabel = "Used",
  secondaryLegendLabel = "Remaining",
  icon = null,
  legendPosition = "bottom",
  footertitle = "",
  className = "",
  lacvalue = "",
  status_card = false,
  barHeights = "70%",
  barwidth = "100%",
  valueSuffix = "",
  totalValueSuffix = "",
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const effectiveMainValue =
    chartMainValue !== undefined ? chartMainValue : mainValue;
  const effectiveTotalValue =
    chartTotalValue !== undefined ? chartTotalValue : totalValue;

  const percentage = useMemo(() => {
    if (!effectiveTotalValue || effectiveTotalValue === 0) return "0";
    return ((effectiveMainValue / effectiveTotalValue) * 100).toFixed(0);
  }, [effectiveMainValue, effectiveTotalValue]);

  const chartOptions = useMemo(() => {
    const safeMain =
      typeof effectiveMainValue === "number" ? effectiveMainValue : 0;
    const safeTotal =
      typeof effectiveTotalValue === "number" ? effectiveTotalValue : 0;

    const seriesData =
      safeTotal === 0
        ? [
            { name: primaryLegendLabel || "Primary", data: [0] },
            { name: secondaryLegendLabel || "Secondary", data: [100] },
          ]
        : [
            { name: primaryLegendLabel || "Primary", data: [safeMain] },
            {
              name: secondaryLegendLabel || "Secondary",
              data: [safeTotal - safeMain],
            },
          ];

    return {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        sparkline: { enabled: true },
      },
      series: seriesData,
      colors: [primaryColor, secondaryColor],
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: barHeights,
        },
      },
      xaxis: {
        categories: [mainLabel],
        labels: { show: false },
        axisBorder: { show: false },
      },
      yaxis: {
        labels: { show: false },
        axisBorder: { show: false },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, { seriesIndex }) =>
          seriesIndex === 0 ? `${percentage}%` : "",
        style: {
          fontSize: "12px",
          fontWeight: "bold",
          colors: ["#FFF"],
        },
      },
      legend: { show: false },
      tooltip: { enabled: false },
      grid: { show: false },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    };
  }, [
    effectiveMainValue,
    effectiveTotalValue,
    percentage,
    primaryColor,
    secondaryColor,
    mainLabel,
    primaryLegendLabel,
    secondaryLegendLabel,
    barHeights,
  ]);

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = new ApexCharts(chartRef.current, chartOptions);
        chartInstance.current.render();
      } else {
        chartInstance.current.updateOptions(chartOptions);
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartOptions]);

  return (
    <div className={`w-full ${className}`}>
      {/* Title with Icon */}
      <div
        className="flex items-center gap-2 mb-1"
        style={{ color: THEME_COLORS.green }}
      >
        {icon}
        <h2
          className="font-calibri font-semibold text-base md:text-lg capitalize"
          style={{ color: THEME_COLORS.green }}
        >
          {title}
        </h2>
      </div>

      {/* Chart + Legend Wrapper */}
      <div className="grid grid-cols-1 md:grid-cols-5 items-center gap-4">
        {/* Chart */}
        <div className="w-full md:col-span-3">
          <div ref={chartRef} className="w-full p-2" />
        </div>

        {/* Legend */}
        <div
          className={`w-full md:col-span-2 flex ${
            legendPosition === "right"
              ? "flex-col items-start"
              : "flex-row flex-wrap items-center"
          } gap-3 text-xs md:text-sm md:pl-4`}
        >
          {/* Secondary */}
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: secondaryColor }}
            />
            <span>
              {secondaryLegendLabel}:{" "}
              {status_card
                ? `${totalValue}${totalValueSuffix}`
                : `${percentage}%`}
            </span>
          </div>

          {/* Primary */}
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: primaryColor }}
            />
            <span>
              {primaryLegendLabel}:{" "}
              {status_card
                ? `${mainValue}${valueSuffix}`
                : `${totalValue}${totalValueSuffix}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressChartSupply;
